// SF22.1: the import half of custom rigging. Given an uploaded, pre-rigged
// GLB's downloadUrl, load it and walk its SkinnedMesh.skeleton.bones into a
// flat { name, parent, restTransform } tree - the shape a bone-mapping UI
// (SF22.2) enumerates target/source joints from, and the shape the retarget
// algorithm samples rest poses from. This is the prerequisite only: it does
// not retarget or map anything, it just exposes what the GLB actually is.
//
// Kept deliberately identical to its sibling copy at
// sunder/sstk-sunder-web/src/prototype/src/importRig.ts (see retarget.ts's
// own header for why).
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

/** A joint's runtime name, its parent's runtime name (null for the skeleton
 * root), and its bind-pose local transform as loaded (no animation has
 * played yet, so this is the rig's rest pose). */
export interface ImportedBone {
  name: string;
  parent: string | null;
  restTransform: {
    position: [number, number, number];
    quaternion: [number, number, number, number];
    scale: [number, number, number];
  };
}

function boneRestTransform(bone: THREE.Bone): ImportedBone['restTransform'] {
  return {
    position: [bone.position.x, bone.position.y, bone.position.z],
    quaternion: [bone.quaternion.x, bone.quaternion.y, bone.quaternion.z, bone.quaternion.w],
    scale: [bone.scale.x, bone.scale.y, bone.scale.z],
  };
}

/** Find the first SkinnedMesh in a loaded glTF scene graph, if any. */
export function findSkinnedMesh(root: THREE.Object3D): THREE.SkinnedMesh | null {
  let found: THREE.SkinnedMesh | null = null;
  root.traverse((node) => {
    if (!found && (node as THREE.SkinnedMesh).isSkinnedMesh) found = node as THREE.SkinnedMesh;
  });
  return found;
}

/**
 * Walk a SkinnedMesh's skeleton into a flat bone list, in the skeleton's own
 * bind order. A bone's parent is only recorded when the parent is itself a
 * skinned joint (not an armature/scene node above the skeleton root).
 */
export function walkSkeleton(mesh: THREE.SkinnedMesh): ImportedBone[] {
  const joints = new Set<THREE.Object3D>(mesh.skeleton.bones);
  return mesh.skeleton.bones.map((bone) => ({
    name: bone.name,
    parent: bone.parent && joints.has(bone.parent) ? bone.parent.name : null,
    restTransform: boneRestTransform(bone),
  }));
}

/**
 * Load an uploaded GLB and walk its skeleton. Rejects if it contains no
 * SkinnedMesh - an unrigged asset belongs in the Animation Studio's static-GLB
 * preview (SF19.1), not the custom-rig import flow.
 */
export function loadImportedRig(url: string): Promise<ImportedBone[]> {
  return new Promise((resolve, reject) => {
    new GLTFLoader().load(
      url,
      (gltf) => {
        const mesh = findSkinnedMesh(gltf.scene);
        if (!mesh) {
          reject(new Error('No rigged (SkinnedMesh) geometry found in this GLB.'));
          return;
        }
        resolve(walkSkeleton(mesh));
      },
      undefined,
      (err) => reject(err instanceof Error ? err : new Error(String(err))),
    );
  });
}
